import { apiTimeOffRequest } from "@/api/time-off-request.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook";
import { timeOffAnalyticsKeys } from "@/queries/analytics/use-time-off-analytics.query.ts";
import { employeeTodayKeys } from "@/queries/employees/use-employee-today.query.ts";
import { employeesKeys } from "@/queries/employees/use-employees.query.ts";
import { timeOffsKeys } from "@/queries/use-time-off.query.ts";
import { ApiErrorException } from "@/types/api.types";
import {
  ApiTimeOffApproveRequest,
  ApiTimeOffConfirmationCommentUpdate,
  ApiTimeOffRequestSave,
} from "@/types/time-off/time-off.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const timeOffRequestsKeys = createQueryKeys("timeOffRequests", {
  details: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      const result = await apiTimeOffRequest.getById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useMyTimeOffRequestDetails = (id: number) => {
  return useQuery({
    ...timeOffRequestsKeys.details(id),
    enabled: !!id,
  });
};

export const useTimeOffRequestCreate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (payload: ApiTimeOffRequestSave) => {
      const result = await apiTimeOffRequest.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffRequests._def,
      });
      void queryClient.invalidateQueries({
        queryKey: timeOffsKeys.calendar._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffCategories._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffCalendar._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useTimeOffRequestUpdate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: ApiTimeOffRequestSave }) => {
      const result = await apiTimeOffRequest.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffRequests._def,
      });
      void queryClient.invalidateQueries({
        queryKey: timeOffRequestsKeys.details(variables.id).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: timeOffsKeys.calendar._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffCategories._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffHistory._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffCalendar._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useTimeOffRequestCancel = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiTimeOffRequest.cancel(id);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({
        queryKey: timeOffRequestsKeys.details(id).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: timeOffsKeys.calendar._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffCategories._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffRequests._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffHistory._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffCalendar._def,
      });
      void queryClient.invalidateQueries({
        queryKey: timeOffAnalyticsKeys.historyReport._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTimeOffRequestDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiTimeOffRequest.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({
        queryKey: timeOffRequestsKeys.details(id).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: timeOffsKeys.calendar._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffCategories._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffRequests._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffHistory._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffCalendar._def,
      });
      void queryClient.invalidateQueries({
        queryKey: timeOffAnalyticsKeys.historyReport._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTimeOffConfirmationCommentUpdate = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: ApiTimeOffConfirmationCommentUpdate;
    }) => {
      const result = await apiTimeOffRequest.updateConfirmationComment(id, payload);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({ queryKey: timeOffRequestsKeys.details(id).queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTimeOffRequestAnswer = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      id,
      approve,
      payload,
    }: {
      id: number;
      approve: boolean;
      payload: ApiTimeOffApproveRequest;
    }) => {
      const result = approve
        ? await apiTimeOffRequest.approve(id, payload)
        : await apiTimeOffRequest.reject(id, payload);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({
        queryKey: timeOffRequestsKeys.details(id).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: employeeTodayKeys.getToday._def,
      });
      void queryClient.invalidateQueries({
        queryKey: timeOffsKeys.calendar._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffCategories._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffRequests._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffHistory._def,
      });
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.timeOffCalendar._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
